/**
 * Store of Diğer › Tanıtım gönderimi (v0.1.135): the outreach read model, loaded when the page opens
 * and read again with Yenile. Read only.
 */

import { useSyncExternalStore } from 'react';
import type { TwinApi } from '../api/client.js';
import type { OutreachMessageDecisionRequest, OutreachView } from '../api/outreach.js';
import { useStore } from './context.js';

export interface OutreachState {
  view: OutreachView | null;
  error: string | null;
  loading: boolean;
  /** Audit items whose refutation is being sent ("auditId:itemNo"). */
  busy: readonly string[];
}

export class OutreachStore {
  private state: OutreachState = { view: null, error: null, loading: false, busy: [] };
  private readonly listeners = new Set<() => void>();
  private inFlight: Promise<void> | null = null;

  constructor(private readonly api: TwinApi) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getState = (): OutreachState => this.state;

  private set(patch: Partial<OutreachState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  ensureLoaded(): Promise<void> {
    if (this.state.view !== null || this.inFlight !== null) return this.inFlight ?? Promise.resolve();
    return this.reload();
  }

  reload(): Promise<void> {
    if (this.inFlight !== null) return this.inFlight;
    this.set({ loading: true });
    const run = (async () => {
      try {
        const view = await this.api.getOutreach();
        this.set({ view, error: null, loading: false });
      } catch (error) {
        this.set({ error: error instanceof Error ? error.message : String(error), loading: false });
      }
    })();
    this.inFlight = run;
    void run.finally(() => {
      if (this.inFlight === run) this.inFlight = null;
    });
    return run;
  }

  /** The campaign approval (the hold is done in the panel); returns an error text, or null. */
  approveCampaign(code: string): Promise<string | null> {
    return this.write(`campaign:${code}`, () => this.api.approveOutreachCampaign(code));
  }

  /** Onayla or Reddet on one wave mail; returns an error text, or null. */
  decide(messageId: string, input: OutreachMessageDecisionRequest): Promise<string | null> {
    return this.write(`message:${messageId}`, () => this.api.decideOutreachMessage(messageId, input));
  }

  private async write(key: string, call: () => Promise<unknown>): Promise<string | null> {
    if (this.state.busy.includes(key)) return null;
    this.set({ busy: [...this.state.busy, key] });
    try {
      await call();
      await this.reload();
      return null;
    } catch (error) {
      return error instanceof Error ? error.message : String(error);
    } finally {
      this.set({ busy: this.state.busy.filter((item) => item !== key) });
    }
  }

  /** The refutation step on one audit finding; returns an error text, or null when it went through. */
  async refute(auditId: string, itemNo: number, decision: 'upheld' | 'overturned'): Promise<string | null> {
    const key = `${auditId}:${itemNo}`;
    if (this.state.busy.includes(key)) return null;
    this.set({ busy: [...this.state.busy, key] });
    try {
      await this.api.refuteOutreachAudit(auditId, itemNo, decision);
      await this.reload();
      return null;
    } catch (error) {
      return error instanceof Error ? error.message : String(error);
    } finally {
      this.set({ busy: this.state.busy.filter((item) => item !== key) });
    }
  }
}

export function useOutreach(): { state: OutreachState; store: OutreachStore } {
  const store = useStore().outreach;
  const state = useSyncExternalStore(store.subscribe, store.getState, store.getState);
  return { state, store };
}
